import { Dispatch, FC, SetStateAction, useMemo, useRef, useState } from 'react';
import {
  HeaderGroup,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as CancelIcon } from 'assets/icons/cancel.svg';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';

import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import useClinic from 'hooks/useClinic';
import useClinicInsuranceCarrier from 'hooks/useClinicInsuranceCarrier';
import { highlightText } from 'utils/common';
import InsuranceToggle, {
  INSURANCE_PROPERTY,
} from './InsuranceToggle/InsuranceToggle';
import InNetworkDoctorDropdown from './DoctorDropdown/InNetworkDoctorDropdown';
import OutOfNetworkDoctorDropdown from './DoctorDropdown/OutOfNetworkDoctorDropdown';
import AllProviderRow from './AllProviderRow/AllProviderRow';

const defaultTablePaginationState = {
  currentPage: 1,
  pageSize: 50,
};

interface TableProps {
  sort: 'asc' | 'desc';
  setSort: Dispatch<SetStateAction<'asc' | 'desc'>>;
  sortBy: string;
  setSortBy: Dispatch<SetStateAction<string>>;
  lastSortInfo: {
    sort: 'asc' | 'desc';
    sortBy: string;
  };
  setLastSortInfo: Dispatch<
    SetStateAction<{ sort: 'asc' | 'desc'; sortBy: string }>
  >;
}

const Table: FC<TableProps> = ({ sort, sortBy, setSort, setSortBy }) => {
  const [pageSize, setPageSize] = useState(
    defaultTablePaginationState.pageSize
  );

  const [currentPage, setCurrentPage] = useState(
    defaultTablePaginationState.currentPage
  );

  const [searchingValue, setSearchingValue] = useState('');

  const { data: clinic } = useClinic();

  const timerRef = useRef<number | null>(null);
  const [isInit, setIsInit] = useState(false);

  const { data, isLoading, mutate } = useClinicInsuranceCarrier({
    limit: pageSize,
    page: currentPage,
    sort,
    sortBy,
    clinicIds: clinic ? [clinic.id] : [],
    searchingValue: isInit ? searchingValue : '',
  });

  const columns = useMemo(
    () => [
      {
        Header: 'Insurance Provider',
        accessor: (field: any) => {
          const highlightedParts = highlightText(field.name, searchingValue);

          return highlightedParts.map((part: any, index: number) => (
            <span
              className={part.highlight ? 'text-magenta' : 'text-darkest-grey'}
              key={`part-${index}-${sortBy}-${sort}-${field.id}`}
            >
              {part.text}
            </span>
          ));
        },
        id: 'name',
      },
      {
        Header: 'Accepted Insurance',
        accessor: (field: any) => {
          return (
            <InsuranceToggle
              key={`${sort}-${sortBy}-${field.id}-${field.updatedAt}`}
              insurance={field}
              currentField={INSURANCE_PROPERTY.IS_ACTIVE}
            />
          );
        },
        id: 'isActive',
      },
      {
        Header: 'In Network',
        accessor: (field: any) => {
          const inNetworkDoctorIds = (field.doctors || [])
            .filter((d: any) => d.isInNetwork === true)
            .map((d: any) => d.id);
          const outOfNetworkDoctorIds = (field.doctors || [])
            .filter((d: any) => d.isInNetwork === false)
            .map((d: any) => d.id);

          return (
            <div className="pr-6">
              <InNetworkDoctorDropdown
                insuranceId={field.id}
                selectedDoctorIds={inNetworkDoctorIds}
                disabledDoctorIds={outOfNetworkDoctorIds}
                disabled={!field.isActive}
              />
            </div>
          );
        },
        id: 'inNetworkDoctors',
      },
      {
        Header: 'Out of Network',
        accessor: (field: any) => {
          const inNetworkDoctorIds = (field.doctors || [])
            .filter((d: any) => d.isInNetwork === true)
            .map((d: any) => d.id);
          const outOfNetworkDoctorIds = (field.doctors || [])
            .filter((d: any) => d.isInNetwork === false)
            .map((d: any) => d.id);

          return (
            <div className="pr-6">
              <OutOfNetworkDoctorDropdown
                insuranceId={field.id}
                selectedDoctorIds={outOfNetworkDoctorIds}
                disabledDoctorIds={inNetworkDoctorIds}
                disabled={!field.isActive}
              />
            </div>
          );
        },
        id: 'outOfNetworkDoctors',
      },
    ],
    [searchingValue, sortBy, sort]
  );

  const dataTable = useMemo(() => {
    if (isLoading || !data.data) {
      return { data: [], metadata: null };
    }

    return { data: data.data, metadata: data.metadata };
  }, [data?.data, data?.metadata, isLoading]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data: dataTable.data ? dataTable.data : [],
        manualPagination: true,
        pageCount: dataTable.metadata ? data.metadata.total : 0,
      },
      useSortBy,
      usePagination,
      useRowSelect,
      useFlexLayout,
      (hooks) => {
        hooks.visibleColumns.push((columns) => [...columns]);
      }
    );

  const handlePaginating = async (tableData: ITablePaginate) => {
    setCurrentPage(tableData.perPage === pageSize ? tableData.currPage : 1);
    setPageSize(tableData.perPage);
    // RESET CACHE
    await mutate(undefined, { revalidate: false });
  };

  const handleSortColumn = async (column: HeaderGroup<object>) => {
    setCurrentPage(defaultTablePaginationState.currentPage);
    setPageSize(defaultTablePaginationState.pageSize);
    setSortBy(column.id);
    if (column.id === sortBy) {
      return setSort((sort) => (sort === 'asc' ? 'desc' : 'asc'));
    }
    setSort('asc');
    // RESET CACHE
    await mutate(undefined, { revalidate: false });
  };

  const delayUpdateSearchRef = (value: string) => {
    const timer = window.setTimeout(() => {
      if (timerRef.current !== timer) return;
      if (!value) return;

      setCurrentPage(defaultTablePaginationState.currentPage);
      setPageSize(defaultTablePaginationState.pageSize);
      setIsInit(true);
    }, 300);

    timerRef.current = timer;
  };

  const searchInsurance = (event: any) => {
    const value = event.target.value;
    setIsInit(false);
    setSearchingValue(value);
    delayUpdateSearchRef(value);
  };

  const onClearSearch = () => {
    setSearchingValue('');
    setIsInit(false);
    setCurrentPage(defaultTablePaginationState.currentPage);
    setPageSize(defaultTablePaginationState.pageSize);
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[37rem] flex justify-center items-center">
                <LoadingSpinner className="all-child:fill-magenta" />
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    if (data.metadata.total === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
                <EmptyBucket />
                {clinic.isInsuranceEnabled ? (
                  <span>No insurance available on this list</span>
                ) : (
                  <span>
                    Insurance collection during booking is turned off.
                  </span>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody {...getTableBodyProps()} className="text-darkest-grey">
        {/* LIVE OUTSIDE REACT_TABLE */}
        <AllProviderRow />
        {page.map((row: any) => {
          prepareRow(row);
          return (
            <tr
              {...row.getRowProps()}
              className="bg-white border-b border-light-grey px-4 group hover:bg-lightPink items-center cursor-pointer"
            >
              {row.cells.map((cell: any, index: number) => {
                return (
                  <td
                    className={`text-left p-0 py-1.9 my-auto ${
                      index === 0 ? 'pl-2.4' : ''
                    }`}
                    {...cell.getCellProps()}
                    key={`cell-${index}`}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    );
  };

  return (
    <>
      <div className="flex flex-row item-centers mb-[1.6rem] w-full max-w-[70rem] min-h-[4.8] rounded-[0.8rem] border border-lightest-grey shadow-elevation-07 py-[13px] px-[19px]">
        <SearchIcon className="mr-[1rem]" width={20} />
        <input
          value={searchingValue}
          placeholder="Search insurance providers..."
          className="text-14 placeholder-mid-grey focus:outline-none focus-visible:outline-none w-full"
          onChange={searchInsurance}
          onKeyDown={(e) => e.stopPropagation()}
        />
        {searchingValue && (
          <CancelIcon
            className="ml-[1rem] cursor-pointer stroke-white"
            width={20}
            onClick={onClearSearch}
          />
        )}
      </div>

      <div className="mb-[2.9rem] mt-[2.4rem] rounded-[0.8rem] border border-light-grey shadow-elevation-07">
        <table
          {...getTableProps()}
          className="w-full text-14 leading-[2.1rem] min-h-[51.2rem]"
        >
          <thead className="font-bold">
            {headerGroups.map((headerGroup, index) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className="px-4 items-center"
                key={`header-group-${index}`}
              >
                {headerGroup.headers.map((column, index) => {
                  return (
                    <th
                      {...column.getHeaderProps()}
                      className="text-left p-0 py-1.9"
                      key={`header-${index}`}
                    >
                      <div
                        className={`flex items-center gap-x-0.9 ${
                          column.id !== 'inNetworkDoctors' &&
                          column.id !== 'outOfNetworkDoctors'
                            ? 'cursor-pointer'
                            : ''
                        }`}
                        onClick={() =>
                          column.id !== 'inNetworkDoctors' &&
                          column.id !== 'outOfNetworkDoctors' &&
                          handleSortColumn(column)
                        }
                      >
                        <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                          {column.render('Header')}
                        </span>

                        {column.id !== 'inNetworkDoctors' &&
                          column.id !== 'outOfNetworkDoctors' && (
                            <span>
                              <SortArrow
                                className={`${
                                  column.id === sortBy && 'fill-magenta'
                                } ${
                                  column.id === sortBy &&
                                  sort === 'asc' &&
                                  'rotate-180'
                                } transition-all`}
                              />
                            </span>
                          )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {renderTableBody()}
        </table>
        {dataTable.metadata && dataTable.metadata.total > 0 && (
          <div className="py-1.6">
            <Pagination
              isBorderNotIncluded
              handlePaginating={handlePaginating}
              data={{
                currPage: dataTable.metadata.page,
                perPage: dataTable.metadata.limit,
                total: dataTable.metadata.total,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Table;
